import "./App.css";
import { lazy, Suspense } from "react";
import { AuthProvider } from "./components/contexts/AuthContext";
import { UploadProvider } from "./components/contexts/UploadContext";
import UploadProgressDock from "./components/utils/UploadProgressDock";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import SentryErrorBoundary from "./components/utils/SentryErrorBoundary";
import Home from "./components/pages/Home/Home";
import AuthRouteUser from "./components/contexts/AuthRouteUser";
import { Analytics } from "@vercel/analytics/react";
import { EditorProvider } from "./components/contexts/EditorContext";
import NavBarConditionalComponent from "./components/navbar/NavBarConditionalComponent";
import EditorNavBar from "./components/navbar/EditorNavBar";
import { GoogleOAuthProvider } from "@react-oauth/google";
import AuthProtectedNoUseRoute from "./components/contexts/AuthProtectedNoUseRoute";
import ScrollToTop from "./components/utils/ScrollToTop";
import BlogPages from "./components/pages/Blog/BlogPages";

// Route components are code-split with React.lazy so the heavy screens
// (editor + ffmpeg, timeline, calendar, dashboards) are fetched on demand
// instead of being bundled into the initial download. This keeps the first
// load light — critical for lower-powered phones and mobile networks.
const Pricing = lazy(() => import("./components/pages/Pricing/Pricing"));
const LoginForm = lazy(() => import("./components/pages/Login/LoginForm"));
const Dashboard = lazy(() => import("./components/pages/Dashboard/Dashboard"));
const ContentSchedulerPage = lazy(() =>
  import("./components/pages/Calendar/Calendar")
);
const FilePage = lazy(() =>
  import("./components/pages/FilePageProcess/FilePage")
);
const FileWaitPage = lazy(() =>
  import("./components/pages/FilePageProcess/FileWaitPage")
);
const EditorPage = lazy(() => import("./components/pages/Editor/EditorPage"));
const Verify = lazy(() => import("./components/pages/Verify"));
const ResetPasswordRequest = lazy(() =>
  import("./components/pages/ResetPasswordRequest")
);
const ResetPassword = lazy(() => import("./components/pages/ResetPassword"));
const Exports = lazy(() => import("./components/pages/Exports"));
const YoutubeDemoneitzationExplainerPage = lazy(() =>
  import("./components/pages/YoutubeDemoneitzationExplainerPage")
);
const TermsOfUse = lazy(() => import("./components/pages/Legal/TermsOfUse"));
const EuropeanLawGDPR = lazy(() =>
  import("./components/pages/Legal/EuropeanLawGDPR")
);
const PrivacyPolicy = lazy(() =>
  import("./components/pages/Legal/PrivacyPolicy")
);
const Features = lazy(() => import("./components/pages/Features/Features"));
const ProfanityMuting = lazy(() =>
  import("./components/pages/Features/ProfanityMuting")
);
const LivestreamProfanityFilter = lazy(() =>
  import("./components/pages/Features/LivestreamProfanityFilter")
);
const OBSIntegration = lazy(() =>
  import("./components/pages/Features/OBSIntegration")
);
const ManageSubscriptions = lazy(() =>
  import("./components/pages/ManageSubscriptions")
);
const PaymentMethodAdded = lazy(() =>
  import("./components/pages/StripeFlow/PaymentMethodAdded")
);
const SubscriptionFailed = lazy(() =>
  import("./components/pages/StripeFlow/SubscriptionFailed")
);
const SuccessPage = lazy(() => import("./components/pages/Success"));
const EmailVerificationPage = lazy(() =>
  import("./components/pages/SendEmailVerfication")
);
const PageNotFound = lazy(() => import("./components/pages/PageNotFound"));
const ContactPage = lazy(() => import("./components/pages/Contact"));
const ContactSales = lazy(() =>
  import("./components/pages/ContactSales/ContactSales")
);
const FaceBlurring = lazy(() =>
  import("./components/pages/Features/FaceBlurring")
);
const ImageCensoring = lazy(() =>
  import("./components/pages/Features/ImageCensoring")
);
const AffiliatePage = lazy(() => import("./components/pages/ReferralPage"));
const ReferralPage = lazy(() => import("./components/pages/Ref/Ref"));
const SettingsPage = lazy(() =>
  import("./components/pages/Settings/Settings")
);
const ZenstreamVsKlap = lazy(() =>
  import("./components/pages/Compare/ZenstreamVsKlap")
);
const ZenstreamVsOpusClip = lazy(() =>
  import("./components/pages/Compare/ZenstreamVsOpusClip")
);
const ZenstreamVsVidyo = lazy(() =>
  import("./components/pages/Compare/ZenstreamVsVidyo")
);
const ZenstreamVsGling = lazy(() =>
  import("./components/pages/Compare/ZenstreamVsGling")
);
const ZenstreamVsDescript = lazy(() =>
  import("./components/pages/Compare/ZenstreamVsDescript")
);
const ZenstreamVsSubmagic = lazy(() =>
  import("./components/pages/Compare/ZenstreamVsSubmagic")
);
const ZenstreamVsKapwing = lazy(() =>
  import("./components/pages/Compare/ZenstreamVsKapwing")
);
const ZenstreamVsPictory = lazy(() =>
  import("./components/pages/Compare/ZenstreamVsPictory")
);
const ZenstreamVsVizard = lazy(() =>
  import("./components/pages/Compare/ZenstreamVsVizard")
);
const ZenstreamVsVeed = lazy(() =>
  import("./components/pages/Compare/ZenstreamVsVeed")
);
const ZenstreamVsCaptions = lazy(() =>
  import("./components/pages/Compare/ZenstreamVsCaptions")
);
const ZenstreamVsRiverside = lazy(() =>
  import("./components/pages/Compare/ZenstreamVsRiverside")
);
const ZenstreamVsEklipse = lazy(() =>
  import("./components/pages/Compare/ZenstreamVsEklipse")
);
const CompareIndex = lazy(() =>
  import("./components/pages/Compare/CompareIndex")
);
const ProfanityFilterYouTube = lazy(() =>
  import("./components/pages/UseCases/ProfanityFilterYouTube")
);
const BlurFacesVideo = lazy(() =>
  import("./components/pages/UseCases/BlurFacesVideo")
);
const YouTubeDemonetizationPrevention = lazy(() =>
  import("./components/pages/UseCases/YouTubeDemonetizationPrevention")
);
const AIVideoClippingCreators = lazy(() =>
  import("./components/pages/UseCases/AIVideoClippingCreators")
);
const ContentModerationCreators = lazy(() =>
  import("./components/pages/UseCases/ContentModerationCreators")
);
const PodcastToShortClips = lazy(() =>
  import("./components/pages/UseCases/PodcastToShortClips")
);
const FixYouTubeYellowIcon = lazy(() =>
  import("./components/pages/UseCases/FixYouTubeYellowIcon")
);
const CorporateVideoCompliance = lazy(() =>
  import("./components/pages/UseCases/CorporateVideoCompliance")
);
const ChurchMinistryVideoContent = lazy(() =>
  import("./components/pages/UseCases/ChurchMinistryVideoContent")
);
const NaturalLanguageVideoEditing = lazy(() =>
  import("./components/pages/UseCases/NaturalLanguageVideoEditing")
);
const DeleteAccountPage = lazy(() =>
  import("./components/pages/DeleteAccount/DeleteAccount")
);
const EnterpriseSalesPage = lazy(() =>
  import("./components/pages/Enterprise/EnterpriseSalesPage")
);
const EnterpriseDashboard = lazy(() =>
  import("./components/pages/Enterprise/EnterpriseDashboard")
);
const TwitchAutomation = lazy(() =>
  import("./components/pages/TwitchAutomation/TwitchAutomation")
);
const LearningCenter = lazy(() =>
  import("./components/pages/LearningCenter/LearningCenter")
);
const LivestreamPage = lazy(() => import("./components/pages/Livestreams"));
const LivestreamStudio = lazy(() =>
  import("./components/pages/Livestreams/LivestreamStudio")
);
const SolutionsIndex = lazy(() =>
  import("./components/pages/Solutions/SolutionsIndex")
);
const SolutionPage = lazy(() =>
  import("./components/pages/Solutions/SolutionPage")
);

// Lightweight fallback shown while a route chunk loads. Kept dependency-free
// (no MUI/antd) so it never blocks first paint.
function RouteFallback() {
  return (
    <div
      className="flex min-h-screen w-full items-center justify-center bg-gray-50"
      role="status"
      aria-label="Loading"
    >
      <span className="h-8 w-8 animate-spin rounded-full border-2 border-gray-300 border-t-gray-800" />
    </div>
  );
}

function App() {
  const googleClientId = process.env.REACT_APP_OAUTH_GOOGLE_CLIENT_ID;

  return (
    <>
      <GoogleOAuthProvider clientId={googleClientId}>
        <Router>
          <ScrollToTop />
          <Analytics />

          <SentryErrorBoundary>
            <div className="bg-gray-50">
              <AuthProvider>
                <UploadProvider>
                <NavBarConditionalComponent />
                <UploadProgressDock />

              <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/legal/terms_of_use" element={<TermsOfUse />} />
                <Route
                  path="/legal/european_union_gdpr"
                  element={<EuropeanLawGDPR />}
                />
                <Route
                  path="/legal/privacy_policy"
                  element={<PrivacyPolicy />}
                />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/solutions" element={<SolutionsIndex />} />
                <Route
                  path="/solutions/:slug"
                  element={<SolutionPage />}
                />
                <Route
                  path="/features/face_blurring"
                  element={<FaceBlurring />}
                />
                <Route path="/features" element={<Features />} />
                <Route
                  path="/features/image_blurring"
                  element={<ImageCensoring />}
                />

                <Route
                  path="/features/profanity_filter"
                  element={<ProfanityMuting />}
                />
                <Route
                  path="/features/livestream_profanity_filter"
                  element={<LivestreamProfanityFilter />}
                />
                <Route
                  path="/features/obs_integration"
                  element={<OBSIntegration />}
                />
                <Route path="/compare/klap" element={<ZenstreamVsKlap />} />
                <Route path="/compare/opus-clip" element={<ZenstreamVsOpusClip />} />
                <Route path="/compare/vidyo-ai" element={<ZenstreamVsVidyo />} />
                <Route path="/compare/gling" element={<ZenstreamVsGling />} />
                <Route path="/compare/descript" element={<ZenstreamVsDescript />} />
                <Route path="/compare/submagic" element={<ZenstreamVsSubmagic />} />
                <Route path="/compare/kapwing" element={<ZenstreamVsKapwing />} />
                <Route path="/compare/pictory" element={<ZenstreamVsPictory />} />
                <Route path="/compare/vizard" element={<ZenstreamVsVizard />} />
                <Route path="/compare/veed" element={<ZenstreamVsVeed />} />
                <Route path="/compare/captions" element={<ZenstreamVsCaptions />} />
                <Route path="/compare/riverside" element={<ZenstreamVsRiverside />} />
                <Route path="/compare/eklipse" element={<ZenstreamVsEklipse />} />
                <Route path="/compare" element={<CompareIndex />} />
                <Route path="/use-cases/profanity-filter-youtube" element={<ProfanityFilterYouTube />} />
                <Route path="/use-cases/blur-faces-video" element={<BlurFacesVideo />} />
                <Route path="/use-cases/youtube-demonetization-prevention" element={<YouTubeDemonetizationPrevention />} />
                <Route path="/use-cases/ai-video-clipping" element={<AIVideoClippingCreators />} />
                <Route path="/use-cases/content-moderation-creators" element={<ContentModerationCreators />} />
                <Route path="/use-cases/podcast-to-short-clips" element={<PodcastToShortClips />} />
                <Route path="/use-cases/fix-youtube-yellow-icon" element={<FixYouTubeYellowIcon />} />
                <Route path="/use-cases/corporate-video-compliance" element={<CorporateVideoCompliance />} />
                <Route path="/use-cases/church-ministry-video-content" element={<ChurchMinistryVideoContent />} />
                <Route path="/use-cases/natural-language-video-editing" element={<NaturalLanguageVideoEditing />} />
                <Route
                  path="/enterprise"
                  element={
                    <AuthRouteUser>
                      <EnterpriseDashboard />
                    </AuthRouteUser>
                  }
                />
                <Route path="/enterprise/sales" element={<EnterpriseSalesPage />} />
                <Route path="/contact-sales" element={<ContactSales />} />
                <Route path="/livestream" element={<LivestreamPage/>}/>
                <Route
                  path="/livestream/studio"
                  element={
                    <AuthRouteUser>
                      <LivestreamStudio />
                    </AuthRouteUser>
                  }
                />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/learning-center" element={<LearningCenter />} />
                <Route
                  path="/login"
                  element={
                    <AuthProtectedNoUseRoute>
                      <LoginForm />
                    </AuthProtectedNoUseRoute>
                  }
                />
                <Route
                  path="/ref"
                  element={
                    <AuthProtectedNoUseRoute>
                      <ReferralPage />
                    </AuthProtectedNoUseRoute>
                  }
                />

                <Route
                  path="/send-email-verification"
                  element={
                    <AuthRouteUser>
                      <EmailVerificationPage />
                    </AuthRouteUser>
                  }
                />
                <Route path="/verify/:token" element={<Verify />} />
                {/* Protected routes */}

                <Route
                  path="/reset_password"
                  element={<ResetPasswordRequest />}
                />
                <Route
                  path="/reset_password/:token"
                  element={<ResetPassword />}
                />
                <Route
                  path="/calendar"
                  element={
                    <AuthRouteUser>
                      {" "}
                      <ContentSchedulerPage />{" "}
                    </AuthRouteUser>
                  }
                />
                <Route
                  path="/dashboard"
                  element={
                    <AuthRouteUser>
                      <Dashboard />
                    </AuthRouteUser>
                  }
                />
                <Route
                  path="/videos/:id"
                  element={
                    <AuthRouteUser>
                      <FilePage />
                    </AuthRouteUser>
                  }
                />
                <Route
                  path="/exports"
                  element={
                    <AuthRouteUser>
                      <Exports />
                    </AuthRouteUser>
                  }
                />
                <Route
                  path="/uploading_file"
                  element={
                    <AuthRouteUser>
                      <FileWaitPage />
                    </AuthRouteUser>
                  }
                />

                <Route
                  path="/editor/:id"
                  element={
                    <AuthRouteUser>
                      <EditorProvider>
                        <EditorNavBar />
                        <EditorPage />
                      </EditorProvider>
                    </AuthRouteUser>
                  }
                />

                <Route path="/affiliate/" element={<AffiliatePage />} />
                <Route
                  path="/manage-subscriptions"
                  element={
                    <AuthRouteUser>
                      <ManageSubscriptions />
                    </AuthRouteUser>
                  }
                />
                <Route
                  path="/settings"
                  element={
                    <AuthRouteUser>
                      <SettingsPage />
                    </AuthRouteUser>
                  }
                />
                <Route
                  path="/twitch-automation"
                  element={
                    <AuthRouteUser>
                      <TwitchAutomation />
                    </AuthRouteUser>
                  }
                />
                <Route
                  path="/delete-account"
                  element={
                    <AuthRouteUser>
                      <DeleteAccountPage />
                    </AuthRouteUser>
                  }
                />
                <Route
                  path="/export"
                  element={
                    <AuthRouteUser>
                      <EditorProvider>
                        <Exports />
                      </EditorProvider>
                    </AuthRouteUser>
                  }
                />
                <Route
                  path="/payment-method-added"
                  element={
                    <AuthRouteUser>
                      <PaymentMethodAdded />
                    </AuthRouteUser>
                  }
                />

                <Route
                  path="/subscription-failed"
                  element={
                    <AuthRouteUser>
                      <SubscriptionFailed />
                    </AuthRouteUser>
                  }
                />
                <Route
                  path="/briefs/youtube_guidelines_database"
                  element={<YoutubeDemoneitzationExplainerPage />}
                />
                <Route path="/blog/:blog_title" element={BlogPages} />
                <Route path="/success" element={<SuccessPage />} />

                <Route path="*" element={<PageNotFound />} />
              </Routes>
              </Suspense>
              </UploadProvider>
              </AuthProvider>
            </div>
          </SentryErrorBoundary>
        </Router>
      </GoogleOAuthProvider>
    </>
  );
}

export default App;
